<template>
  <div class="controls">
    <div class="controls__start experiment-header">
      <div class="d-flex justify-content-between align-items-center">
        <span id="experiment-header-title" class="controls__count">{{ currentExperiment ? currentExperiment.name : '' }}</span>
        <EncodedIdField
          :id="currentExperiment?.id"
          :type="'experiment'"
          :hideLabel="true"
          class="ml-2"
        />
      </div>
      <span v-if="isLoading && training_sessions && training_sessions.length > 0" class="controls__count">Training Sessions</span>
      <span v-else class="controls__count">{{ `Training Sessions (${training_sessions.length})` }}</span>
    </div>
    <div class="controls__middle">
      <HelpTip class="ml-2">
        Training session list displays both ongoing and previous sessions associated with a specific experiment.
        You can view details, training metrics (charts), logs, and models by selecting any session card.
        To compare all sessions within the experiment, click on "ALL CHARTS".
      </HelpTip>

      <HelpTip v-if="permissions && !permissions.trainer_write" class="ml-2" help_style="alert">
        This is a read only experiment. Editing functionalities are disabled.
      </HelpTip>
    </div>

    <div class="controls__end">
      <div class="controls__actions">
        <button
          v-if="permissions && (permissions.trainer_read || permissions.trainer_write)"
          id="all-charts-btn"
          class="button"
          @click="handleShowAllChartsClick()"
        >
          <SVGIcon
            :iconName="'analytics'"
            :width="'18px'"
            :height="'18px'"
          />All Charts
        </button>
        <div v-if="permissions && permissions.trainer_write">
          <ActionsButton
            label="Actions"
            :actions="trainerActions"
            buttonStyle="chevron"
            :showCount="true"
            :selectedCount="selectedCount"
            @action-clicked="handleTrainerActionClick"
          />
        </div>
      </div>
    </div>
  </div>
  <div v-if="isLoading" class="empty-state">
    <div id="loading-visualization"><div class="lds-ring"><div /><div /><div /><div /></div></div>
  </div>
  <div v-else-if="!isLoading && training_sessions.length === 0" class="empty-state">
    <EmptyState
      :title="'No training sessions found for this experiment'"
      :subtitle="'To add a new training session, click the New action'"
    />
  </div>
  <template v-else-if="!isLoading && training_sessions.length > 0">
    <div class="trainers-card-container scrollbar">
      <TrainerCard
        v-for="session in training_sessions"
        :key="session.id"
        :session="session"
        :currentProject="currentProject"
        :permissions="permissions"
        @info-clicked="(session) => handleInfoClicked(session)"
        @delete-session="openConfirmDeleteSessionModal"
        @terminate-session="openConfirmStopTrainingSessionModal"
        @delete-trainer="openConfirmDeleteTrainerModal(t)"
        @charts-clicked="(session) =>handleSessionChartsClicked(session)"
        @refresh-session="(session) => handleRefreshSession(session)"
        @clone-training-session="(session) => openTrainerStartModal(session)"
        @resume-training-session="(session) => openConfirmResumeSessionModal(session)"
        @session-selected="handleSessionSelected"
        @edit-trainer="handleEditTrainerClick"
        @description-changed="handleSessionDescriptionChanged"
      />
    </div>
  </template>

  <CreateTrainerSessionModal
    ref="startTrainerSessionModal"
    :trainer="trainerToCreateSession"
    :experiments="experiments"
    :trainerSession="sourceTrainingSession"
    @create-trainer-session="handleTrainingSessionCreated(trainerToCreateSession)"
    @closed="handleTrainerStartModalClosed"
  />
  <TrainingSessionDetailsModal
    ref="trainingSessionDetailsModal"
    :session="selectedTrainingSession"
    :permissions="permissions"
    @stop-training="openConfirmStopTrainingSessionModal"
    @delete-session="openConfirmDeleteSessionModal"
  />
  <TrainingSessionAnalyticsModal
    ref="trainingSessionAnalyticsModal"
    :trainerSession="trainerToShowAnalytics"
    :projectID="currentProject ? currentProject.id : 0"
  />
  <ConfirmModal
    ref="confirmModal"
    :messageHeader="confirmMessageHeader"
    :message="confirmMessage"
    :buttonClass="'button-delete'"
    :buttonText="buttonText"
    @confirmed="confirmFunction"
  />
  <SessionMoveDialog
    ref="sessionMoveDialog"
    :selectedSessions="selectedSessionsObjects"
    :currentTrainerId="currentExperiment?.id"
    @move-sessions="RefreshList"
  />
  <SessionDeleteDialog
    ref="sessionDeleteDialog"
    :selectedSessions="selectedSessionsObjects"
    @delete-sessions="handleDeleteSessionsConfirmed"
  />
  <SessionEditDialog
    ref="sessionEditDialog"
    :selectedSessions="selectedSessionsObjects"
    @edit-session="handleEditSessionConfirmed"
  />
  <AppStartModal
    ref="modelpackAppModal"
    appName="modelpack"
    :defaultValues="{}"
  />
</template>

<script>
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import ConfirmModal from '@/components/ConfirmModal.vue';
import TrainerCard from '@/components/TrainerComponent/TrainerExperimentAndSessions/TrainerCardV2.vue';
import TrainingSessionDetailsModal from '@/components/TrainerComponent/TrainerExperimentAndSessions/TrainingSessionDetailsModalV2.vue';
import TrainingSessionAnalyticsModal from '@/components/TrainerComponent/TrainerExperimentAndSessions/TrainingSessionAnalyticsModal.vue';
import CreateTrainerSessionModal from '@/components/TrainerComponent/TrainerExperimentAndSessions/CreateTrainerSessionModalV2.vue';
import SessionMoveDialog from '@/components/TrainerComponent/TrainerExperimentAndSessions/SessionMoveDialog.vue';
import SessionDeleteDialog from '@/components/TrainerComponent/TrainerExperimentAndSessions/SessionDeleteDialog.vue';
import SessionEditDialog from '@/components/TrainerComponent/TrainerExperimentAndSessions/SessionEditDialog.vue';
import EncodedIdField from '@/components/EncodedIdField.vue';
import SVGIcon from '@/components/SVGIcon.vue';
import ActionsButton from '@/components/ActionsButton.vue';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';
import EmptyState from '@/components/EmptyState.vue';
import HelpTip from '../../HelpTip.vue';
import { decodeID, encodeID } from '@/assets/js/utils.js';
import { goto_training_charts, goto_trainer_session } from '@/assets/js/links.js';
import AppStartModal from '@/components/Apps/AppStartModal.vue';

const $toast = useToast();

export default {
  name: "TrainerSessionManagement",
  components: {
    TrainerCard,
    CreateTrainerSessionModal,
    TrainingSessionDetailsModal,
    TrainingSessionAnalyticsModal,
    ConfirmModal,
    EncodedIdField,
    SVGIcon,
    ActionsButton,
    HelpTip,
    EmptyState,
    SessionMoveDialog,
    SessionDeleteDialog,
    SessionEditDialog,
    AppStartModal,
  },
  inject: {
    experiments: { default: null },
    isPublic: { default: false },
  },
  props: {
    projectId: [String, Number],
    expId: [String, Number],
  },
  emits: [
    'trainer-set-created', 'update-list',
  ],
  data() {
    return {
      dataConnect: null,
      isLoading: true,
      training_sessions: [],
      trainingSessionList: [],
      trainerToCreateSession: null,
      trainerToShowAnalytics: null,
      loadingFinished: true,
      selectedTrainingSession: null,
      selectedTrainingExperiment: null,
      selectedTrainerDetails: null,
      interval: null,
      confirmMessage: null,
      confirmMessageHeader: null,
      toDelete: null,
      confirmFunction: null,
      buttonText: 'Delete',
      modalTrainer: null,
      permissions: null,
      openedDropdowns: [],
      sourceTrainingSession: null,
      selectedSessions: {},
      currentExperiment: null,
      trainerActions: [
        { icon: 'add', label: 'New', eventKey: 'new', tooltip: 'Create New Training Session' },
        { icon: 'model', label: 'New Modelpack Trainer', eventKey: 'new-modelpack', tooltip: 'Start a Modelpack Trainer app' },
        { icon: 'folder2', label: 'Move', eventKey: 'move', tooltip: 'Move selected training sessions to another experiment' },
        { icon: 'edit', label: 'Edit', eventKey: 'edit', tooltip: 'Edit selected training session' },
        { icon: 'chart', label: 'Profile', eventKey: 'profile', tooltip: 'View training session profile' },
        { icon: 'delete', label: 'Delete', eventKey: 'delete', isAlert: true, tooltip: 'Delete selected training sessions' },
      ],
    };
  },
  computed: {
    currentProject() {
      return this.$store.state.projects.currentProject;
    },
    selectedSessionsObjects() {
      return this.training_sessions.filter(
        (session) => this.selectedSessions[session.id]
      );
    },
    selectedCount() {
      return Object.keys(this.selectedSessions).length;
    },
    currentSession: {
      get() {
        return this.$store.state.experiments.currentSession;
      },
      set(session) {
        this.$store.commit('experiments/setCurrentSession', session);
      },
    },
  },
  watch: {
  },
  async created() {
    this.dataConnect = new DatastoreConnect(this.$store.state.enterpriseServerUrl);
  },
  beforeUnmount() {
    this.stopInterval();
  },
  async mounted() {
    // Get expId from route params and decode it if needed
    const expIdParam = this.$route.params.expId;
    const decimalExpId = expIdParam ? decodeID(expIdParam) || parseInt(expIdParam, 10) : null;
    
    if (decimalExpId) {
      await this.handleGetExperiment(decimalExpId);
    }

    this.startInterval();
    this.getProjectAccessForSingleUser();
  },
  methods: {
    async getTrainingSessions(id, showLoading = true) {
      if (!this.training_sessions || this.training_sessions.length === 0) {
        if (showLoading) this.isLoading = true;
      }
      
      const newSessions = await this.getTrainingSessionList(id);
      
      if (!this.training_sessions || this.training_sessions.length === 0) {
        this.training_sessions = newSessions;
      } else {
        // Update existing sessions and add new ones without replacing the array
        const existingIds = new Set(this.training_sessions.map(s => s.id));
        const newIds = new Set(newSessions.map(s => s.id));
        
        // Update or add sessions
        for (const newSession of newSessions) {
          const existingIndex = this.training_sessions.findIndex(s => s.id === newSession.id);
          if (existingIndex >= 0) {
            // Update existing session properties
            Object.assign(this.training_sessions[existingIndex], newSession);
          } else {
            // Add new session
            this.training_sessions.unshift(newSession);
          }
        }
        
        // Remove sessions that no longer exist
        for (let i = this.training_sessions.length - 1; i >= 0; i--) {
          if (!newIds.has(this.training_sessions[i].id)) {
            this.training_sessions.splice(i, 1);
          }
        }
      }
      
      if (showLoading) this.isLoading = false;
    },
    async getProjectAccessForSingleUser() {
      if (this.currentProject && this.$store.state.user?.user?.username) {
        await this.dataConnect.getProjectAccessForSingleUser({
          project_id: this.currentProject.id,
          username: this.$store.state.user.user.username,
        })
          .then((data) => {
            if (data.result) {
              this.permissions = data.result;
            } else if (data.error) {
              $toast.open({
                queue: true,
                dismissible: true,
                message: `Failed to retrieve project access: ${data.error.message}`,
                type: "error",
                position: "top",
              });
            }
          })
          .catch((e) => {
            $toast.open({
              queue: true,
              dismissible: true,
              message: `Failed to retrieve project access: ${e}`,
              type: "error",
              position: "top",
            });
          });
      }
    },

    openConfirmStopTrainingSessionModal(session) {
      this.confirmMessage = `Are you sure you want to stop training session "${session.name}"?\n`;
      this.confirmMessageHeader = 'Stop Training Session';
      this.toDelete = session;
      this.buttonText = 'Stop';
      this.$refs.confirmModal.showModal();
      this.confirmFunction = () => {
        this.$refs.trainingSessionDetailsModal.closeModal();
        this.handleStopTrainingSessions([session.id]);
      };
    },
    openConfirmDeleteSessionModal(session) {
      this.confirmMessage = `Are you sure you want to move training session "${session.name}" to recycle bin? This will also move training artifacts and validate sessions to recycle bin.`;
      this.confirmMessageHeader = 'Remove Training Session';
      this.toDelete = session;
      this.buttonText = 'Remove';
      this.$refs.confirmModal.showModal();
      this.confirmFunction = () => {
        this.$refs.trainingSessionDetailsModal.closeModal();
        this.handleDeleteSession(session.id);
      };
    },
     openConfirmResumeSessionModal(session) {
      this.confirmMessage = `Are you sure you want to resume training session "${session.name}"?`;
      this.confirmMessageHeader = 'Resume Training Session';
      this.toDelete = session;
      this.buttonText = 'Resume';
      this.$refs.confirmModal.showModal();
      this.confirmFunction = () => {
        this.$refs.trainingSessionDetailsModal.closeModal();
        this.handleResumeSession(session.id);
      };
    },
    handlePauseClicked() {
    },
    async handleRefreshSession(session) {
      const index = this.training_sessions.findIndex((e) => e.id === session.id);
      if (index > -1) {
        this.training_sessions[index] = await this.getSession(session.id);
      }
    },
    startInterval() {
      this.interval = setInterval(() => {
        if (this.currentExperiment && this.currentExperiment.id) {
          this.getTrainingSessions(this.currentExperiment.id, false);
        }
      }, 30000);
    },
    stopInterval() {
      clearInterval(this.interval);
      this.interval = null;
    },
    async updateEC2InstanceStatus() {
      await this.dataConnect.updateEC2Status({
        trainer_ids: this.trainerIds,
      })
        .catch((e) => console.log(e));
    },
    async handleTrainerCreated() {
      this.getTrainingSessions(this.currentExperiment.id);
    },
    async getTrainingSessionList(trainer_id) {
      const resp = await this.dataConnect.getTrainingSessionList({ trainer_id, reverse: true })
        .catch((error) => {
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Failed to retrieve training sessions: ${error}`,
            type: "error",
            position: "top",
          });
        });
      if (!resp || resp.error || !resp.result) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: `Failed to retrieve training sessions: ${resp.error.message}`,
          type: "error",
          position: "top",
        });
        return [];
      }

      if (resp.result) {
        resp.result.forEach((session) => {
          if (this.selectedTrainingSession && session.id === this.selectedTrainingSession.id) {
            this.selectedTrainingSession = session;
          }
        });
      }
      return resp.result;
    },
    async getSession(id) {
      const resp = await this.dataConnect.getTrainingSession({
        trainer_session_id: id,
      })
        .catch((e) => {
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Failed to retrieve training session: ${e}`,
            type: "error",
            position: "top",
          });
        });
      if (!resp || resp.error || !resp.result) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: `Failed to retrieve training session: ${resp.error.message}`,
          type: "error",
          position: "top",
        });
        return null;
      }
      return resp.result;
    },
    openTrainerStartModal(session) {
      this.trainerToCreateSession = this.currentExperiment.id;
      this.sourceTrainingSession = session;
      this.$refs.startTrainerSessionModal.showModal();
    },
    handleTrainerStartModalClosed() {
      this.sourceTrainingSession = null;
    },
    async handleTrainingSessionCreated() {
      this.getTrainingSessions(this.currentExperiment.id);
    },
    // async handleSessionAnalyticsClick() {
    //   this.trainerToShowAnalytics = { trainer_id: expID };
    //   await this.$nextTick();
    //   this.$refs.trainingSessionAnalyticsModal.showModal();
    // },
    async launchTrainedModelCompare(trainer_ids) {
      this.trainerToShowAnalytics = { trainer_ids };
      await this.$nextTick();
      this.$refs.trainingSessionAnalyticsModal.showModal();
    },
    async handleStopTrainingSessions(ids) {
      await this.dataConnect.terminateTrainingSessions({
        session_ids: ids,
      })
        .then((data) => {
          this.updateEC2InstanceStatus();
          if (data.error) {
            $toast.open({
              queue: true,
              dismissible: true,
              message: `Failed to stop training session: ${data.error.message}`,
              type: "error",
              position: "top",
            });
          }
        })
        .catch((error) => {
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Failed to stop training session: ${error}`,
            type: "error",
            position: "top",
          });
        });

      this.toDelete = null;
    },
    async handleResumeSession(id) {
      await this.dataConnect.resumeTrainingSession({
        session_id: id,
      })
        .then(async (data) => {
          if (data.error) {
            $toast.open({
              queue: true,
              dismissible: true,
              message: `Failed to resume training session: ${data.error.message}`,
              type: "error",
              position: "top",
            });
          } else if (data.result) {
            this.getTrainingSessions(this.currentExperiment.id);
          }
        })
        .catch((error) => {
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Failed to delete training session: ${error}`,
            type: "error",
            position: "top",
          });
        });

      this.toDelete = null;
    },
    async handleGetExperiment(id) {
      await this.dataConnect.getExperiment({ trainer_id: id })
        .then(async (data) => {
          if (data.result) {
            this.currentExperiment = data.result;
            this.getTrainingSessions(this.currentExperiment.id);
          } else if (data.error) {
            $toast.open({
              queue: true,
              dismissible: true,
              message: `Failed to retrieve experiment: ${data.error.message}`,
              type: "error",
              position: "top",
            });
          }
        })
        .catch((error) => {
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Failed to retrieve experiment: ${error}`,
            type: "error",
            position: "top",
          });
        });
    },
    handleInfoClicked(session) {
      this.currentSession = session;
      goto_trainer_session(this.$route.params.projectId, this.currentExperiment.id, session.id);
    },
    handleEditTrainerClick(session) {
      this.$refs.sessionEditDialog.openModal(session);
    },
    handleSessionChartsClicked(session) {
      this.currentSession = session;
      goto_trainer_session(this.$route.params.projectId, this.currentExperiment.id, session.id, { mode: 'charts' });
    },
    handleShowAllChartsClick() {
      goto_training_charts(this.$route.params.projectId, this.currentExperiment.id);
    },
    handleSessionSelected(payload) {
      if (payload.isSelected) {
        this.selectedSessions[payload.sessionId] = true;
      } else {
        delete this.selectedSessions[payload.sessionId];
      }
    },
    toggleActionsDropdown() {
      this.showActionsDropdown = !this.showActionsDropdown;
    },
    getSelectedSessionIds() {
      return Object.keys(this.selectedSessions);
    },
    handleDeleteSelectedSessions() {
      const selectedIds = this.getSelectedSessionIds();
      if (selectedIds.length === 0) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: 'Please select at least one session to delete',
          type: 'warning',
          position: 'top',
        });
        return;
      }
      this.showActionsDropdown = false;
      this.$refs.sessionDeleteDialog.openModal();
    },
    handleMoveSelectedSessions() {
      const selectedIds = this.getSelectedSessionIds();
      if (selectedIds.length === 0) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: 'Please select at least one session to move',
          type: 'warning',
          position: 'top',
        });
        return;
      }
      // Open move dialog with selected sessions
      this.$refs.sessionMoveDialog.openModal();
    },
    handleEditSelectedSessions() {
      const selectedIds = this.getSelectedSessionIds();
      if (selectedIds.length === 0) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: 'Please select a session to edit',
          type: 'warning',
          position: 'top',
        });
        return;
      }
      if (selectedIds.length > 1) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: 'You can only edit one session at a time',
          type: 'warning',
          position: 'top',
        });
        return;
      }
      const selectedSession = this.selectedSessionsObjects[0];
      this.showActionsDropdown = false;
      this.$refs.sessionEditDialog.openModal(selectedSession);
    },
    handleProfileSelectedSession() {
      const selectedIds = this.getSelectedSessionIds();
      if (selectedIds.length === 0) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: 'Please select a session to view profile',
          type: 'warning',
          position: 'top',
        });
        return;
      }
      if (selectedIds.length > 1) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: 'You can only view profile for one session at a time',
          type: 'warning',
          position: 'top',
        });
        return;
      }
      this.$router.push({ path: '/profile' });
    },
    handleTrainerActionClick(eventKey) {
      switch(eventKey) {
        case 'new':
          this.openTrainerStartModal(null);
          break;
        case 'new-modelpack':
          this.$refs.modelpackAppModal.showModal();
          break;
        case 'move':
          this.handleMoveSelectedSessions();
          break;
        case 'edit':
          this.handleEditSelectedSessions();
          break;
        case 'profile':
          this.handleProfileSelectedSession();
          break;
        case 'delete':
          this.handleDeleteSelectedSessions();
          break;
      }
    },
    async handleDeleteMultipleSessions(sessions) {
      try {
        const sessionIds = sessions.map(session => session.id);
        const expId = sessions[0].trainer_id;
        
        await this.dataConnect.deleteTrainingSession({
          session_ids: sessionIds,
          exp_id: expId,
        });
        
        // Remove from selected sessions
        sessions.forEach(session => {
          delete this.selectedSessions[session.id];
        });
        
        $toast.open({
          queue: true,
          dismissible: true,
          message: `Successfully deleted ${sessions.length} session(s)`,
          type: 'success',
          position: 'top',
        });
        
        // Refresh the list
        this.getTrainingSessions(this.currentExperiment.id, false);
      } catch (error) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: `Failed to delete sessions: ${error}`,
          type: 'error',
          position: 'top',
        });
      }
    },
    handleDeleteSessionsConfirmed(payload) {
      if (payload.success) {
        // Clear selected sessions and refresh list
        this.selectedSessions = {};
        this.getTrainingSessions(this.currentExperiment.id, false);
      }
    },
    handleEditSessionConfirmed(editedSession) {
      // Update the session in the local list
      const idx = this.training_sessions.findIndex(s => s.id === editedSession.id);
      if (idx >= 0) {
        this.training_sessions[idx].name = editedSession.name;
        this.training_sessions[idx].description = editedSession.description;
      }
      // Clear selected sessions
      this.selectedSessions = {};
    },
    async handleSessionDescriptionChanged(payload) {
      const session = payload?.session;
      if (!session?.id) {
        return;
      }

      await this.dataConnect.updateTrainingSession({
        id: session.id,
        name: session.docker_task?.name || session.name || '',
        description: payload?.description || '',
      });

      const idx = this.training_sessions.findIndex((s) => s.id === session.id);
      if (idx >= 0) {
        this.training_sessions[idx].description = payload?.description || '';
      }
    },
    async RefreshList(payload) {
      
        this.getTrainingSessions(this.currentExperiment.id, false);
     
    },
  },
};
</script>

<style lang="scss" scoped>

.controls {
  display: flex;
  margin: 20px 20px 0 20px;
  align-items: center;
  gap: 10px;

  &__count {
    font-weight: bold;
    font-size: 1rem;
  }

  &__start {
    display: flex;
    justify-content: flex-start;
    width: fit-content;
    gap: 10px;

    &.experiment-header {
      flex-direction: column;
    }
  }

  &__end {
    display: flex;
    justify-content: flex-end;
    width: fit-content
  }

  &__middle {
    display: flex;
    justify-content: center;
    align-items: center;
    flex: 1;
  }

  &__actions {
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    height: 100%;
  }
}
@media (max-width: $breakpoint-sm) {
  .controls {
    flex-direction: column;
    &__start{
      display: flex;
      align-items: flex-start;
      flex-direction: column;
      width: 100%;
    }

    &__middle{
      display: flex;
      align-items: flex-start;
      flex-direction: column;
      width: 100%;
    }

    &__end{
      display: flex;
      align-items: flex-end;
      flex-direction: column;
      width: 100%;
    }
  }
}
.trainers-card-container {
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  padding: 16px;
  overflow-y: scroll;
  gap: 16px;
}

.actions-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 150px;
  margin-top: 4px;
}

.dropdown-item {
  padding: 10px 16px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f5f5f5;
  }

  &:first-child {
    border-radius: 4px 4px 0 0;
  }

  &:last-child {
    border-radius: 0 0 4px 4px;
  }
}

.trainer-header {
  display: flex;
  flex-direction: row;
  gap: 16px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  width: 100%;
  height: 100%;

  &__message {
    position: absolute;
    display: flex;
    flex-direction: column;
    text-align: center;
    top: 60%;
    @include themify() {
      color: themed('body-text-color');
    }
  }
}

.button {
    background: white;
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 26px;
    padding: 0;
    box-shadow: 1px 2px 4px 1px rgba(0, 0, 0, 0.2), 0 0 1px 1px rgba(0, 0, 0, 1);
    padding: 4px 8px;
    gap: 2px;
    width: fit-content;

    @include themify() {
      color: themed('body-text-color')
    }

    &.highlight {
      background: var(--button-color);
      color: var(--button-text-color);

      svg {
        margin-right: 5px;
      }
    }

    &:hover {
      background: rgba(var(--color-primary-100-rgb), 0.5);
    }

    &.delete:not([disabled]) {
      @include themify() {
        background: themed('button-delete');
        color: themed('button-delete-text');
      }
    }

    &.delete[disabled] {
      border: none;
    }

    &.delete:hover:not([disabled]) {
      @include themify() {
        background: $color-red-300;
        color: themed('button-delete-text');
      }
    }
  }

#actions-btn {
  display: flex;
  align-items: center;
  gap: 6px;
}
</style>
